import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { settingsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { User, Mail, Bell, Volume2, Upload } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0e5f78cf-f454-4b25-b923-9c08cfb591e4/artifacts/lmgq6k8j_file_00000000ca9471f4a6a1a00d93bbed3f.png";

export default function SettingsPage() {
  const { user } = useAuth();
  const [notificationSettings, setNotificationSettings] = useState({
    browser_notifications: true,
    sound_enabled: true,
    custom_sound_url: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.notification_settings) {
      setNotificationSettings(user.notification_settings);
    }
  }, [user]);

  const handleNotificationChange = async (key, value) => {
    const newSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(newSettings);
    
    try {
      await settingsApi.updateNotifications(newSettings);
      toast.success('Paramètres mis à jour');
      
      // Request browser notification permission if enabled
      if (key === 'browser_notifications' && value && 'Notification' in window) {
        Notification.requestPermission();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSoundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('audio/')) {
      toast.error('Veuillez sélectionner un fichier audio');
      return;
    }
    
    setLoading(true);
    try {
      const response = await settingsApi.uploadSound(file);
      setNotificationSettings(prev => ({ ...prev, custom_sound_url: response.data.url }));
      toast.success('Son personnalisé téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setLoading(false);
    }
  };

  const playTestSound = () => {
    const audio = new Audio(notificationSettings.custom_sound_url || '/notification.mp3');
    audio.play().catch(() => toast.error('Impossible de jouer le son'));
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">Paramètres</h1>
        <p className="text-muted-foreground mt-1 text-sm">Gérez votre compte et vos préférences</p>
      </div>

      {/* Profile Section */}
      <Card className="bg-card border-border/40">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-primary/10 flex items-center justify-center">
              <User className="h-4 w-4 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Profil</CardTitle>
              <CardDescription className="text-xs">Vos informations</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Nom</Label>
              <div className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-sm text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                {user?.name || 'Non défini'}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Email</Label>
              <div className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-sm text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                {user?.email || 'Non défini'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Section */}
      <Card className="bg-card border-border/40">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-primary/10 flex items-center justify-center">
              <Bell className="h-4 w-4 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Notifications</CardTitle>
              <CardDescription className="text-xs">Gérez vos alertes</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Browser Notifications */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
            <div>
              <p className="font-medium text-sm">Notifications navigateur</p>
              <p className="text-xs text-muted-foreground">Recevez des alertes même quand l'app est en arrière-plan</p>
            </div>
            <Switch
              checked={notificationSettings.browser_notifications}
              onCheckedChange={(checked) => handleNotificationChange('browser_notifications', checked)}
              data-testid="browser-notifications-switch"
            />
          </div>

          {/* Sound Notifications */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
            <div>
              <p className="font-medium text-sm">Sons d'alerte</p>
              <p className="text-xs text-muted-foreground">Jouez un son lors d'une nouvelle notification</p>
            </div>
            <Switch
              checked={notificationSettings.sound_enabled}
              onCheckedChange={(checked) => handleNotificationChange('sound_enabled', checked)}
              data-testid="sound-notifications-switch"
            />
          </div>

          {/* Custom Sound */}
          {notificationSettings.sound_enabled && (
            <div className="p-3 bg-muted/30 rounded-sm space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Son personnalisé</p>
                  <p className="text-xs text-muted-foreground">
                    {notificationSettings.custom_sound_url ? 'Son personnalisé actif' : 'Utilise le son par défaut'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {notificationSettings.custom_sound_url && (
                    <Button variant="outline" size="sm" onClick={playTestSound} data-testid="test-sound-button">
                      <Volume2 className="h-4 w-4 mr-1" />Tester
                    </Button>
                  )}
                  <Label htmlFor="sound-upload" className="cursor-pointer">
                    <div className="flex items-center gap-1 px-3 py-1.5 bg-secondary text-secondary-foreground rounded-sm text-sm hover:bg-secondary/80 transition-colors">
                      <Upload className="h-4 w-4" />
                      {loading ? 'Upload...' : 'Charger'}
                    </div>
                    <Input
                      id="sound-upload"
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={handleSoundUpload}
                      disabled={loading}
                      data-testid="sound-upload-input"
                    />
                  </Label>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* About Section */}
      <Card className="bg-card border-border/40">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Era Lodge" className="h-10 w-auto" />
            <div>
              <CardTitle className="font-heading text-lg">À propos</CardTitle>
              <CardDescription className="text-xs">Informations sur l'application</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 text-sm text-muted-foreground">
            <p><span className="font-medium text-foreground">Version:</span> 1.0.0</p>
            <p><span className="font-medium text-foreground">Architecture:</span> Multi-propriétés</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
